import { dayKey } from '@qr/shared/paths';
import type { DailyStats } from '@qr/shared/types';
import { Button, Callout, Card, Spinner } from '@qr/ui';
import { useMemo, useState } from 'react';
import {
  AreaTemporal,
  BarrasReparto,
  ColumnasHora,
  StatTile,
  type Reparto,
} from './Charts.js';

/**
 * Analitica de escaneos.
 *
 * Lee los agregados diarios, nunca los eventos en crudo: con un codigo popular
 * serian cientos de miles de documentos para pintar lo mismo.
 *
 * El orden de la pantalla sigue al de las preguntas: cuantos han sido, como han
 * evolucionado, y solo despues quienes son y cuando llegan. Los desgloses
 * importan, pero nadie empieza por ellos.
 */

export type RangoDias = 7 | 30 | 90;

export interface AnalyticsProps {
  readonly dias: readonly (DailyStats & { readonly id: string })[] | null;
  readonly rango: RangoDias;
  readonly onRango: (dias: RangoDias) => void;
  readonly totalHistorico?: number;
  readonly titulo?: string;
  readonly error?: string | null;
  readonly nombreFichero?: string;
}

const NOMBRES_DISPOSITIVO: Record<string, string> = {
  mobile: 'Movil',
  tablet: 'Tablet',
  desktop: 'Ordenador',
  unknown: 'Sin identificar',
};

/**
 * Nombres de pais mas legibles que su codigo.
 *
 * `Intl.DisplayNames` los traduce sin tener que mantener una lista, y si el
 * codigo es raro devuelve el propio codigo en vez de romperse.
 */
function nombrePais(codigo: string): string {
  if (codigo === 'XX') return 'Sin identificar';
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(codigo) ?? codigo;
  } catch {
    return codigo;
  }
}

function sumar(
  dias: readonly DailyStats[],
  campo: 'byCountry' | 'byDevice' | 'byHour',
): Record<string, number> {
  const total: Record<string, number> = {};
  for (const dia of dias) {
    for (const [clave, valor] of Object.entries(dia[campo] ?? {})) {
      total[clave] = (total[clave] ?? 0) + (typeof valor === 'number' ? valor : 0);
    }
  }
  return total;
}

export function ScanAnalytics({
  dias,
  rango,
  onRango,
  totalHistorico,
  titulo = 'Escaneos',
  error = null,
  nombreFichero = 'escaneos',
}: AnalyticsProps) {
  const [tabla, setTabla] = useState(false);

  const datos = useMemo(() => {
    if (!dias) return null;

    const ordenados = [...dias].sort((a, b) => a.day.localeCompare(b.day));

    // Se rellenan los dias sin escaneos: sin ellos, una semana sin actividad
    // se dibujaria como una linea continua y parecería que no paso nada raro.
    const serie: { dia: string; valor: number }[] = [];
    const porDia = new Map(ordenados.map((d) => [d.day, d]));
    const hoy = new Date();

    for (let i = rango - 1; i >= 0; i -= 1) {
      const fecha = new Date(hoy);
      fecha.setUTCDate(fecha.getUTCDate() - i);
      const clave = dayKey(fecha);
      serie.push({ dia: clave, valor: porDia.get(clave)?.scans ?? 0 });
    }

    return {
      serie,
      total: ordenados.reduce((s, d) => s + d.scans, 0),
      unicos: ordenados.reduce((s, d) => s + d.uniqueScans, 0),
      paises: sumar(ordenados, 'byCountry'),
      dispositivos: sumar(ordenados, 'byDevice'),
      horas: sumar(ordenados, 'byHour'),
      ordenados,
    };
  }, [dias, rango]);

  function exportarCsv() {
    if (!datos) return;

    // Punto y coma como separador: es lo que espera Excel en configuracion
    // espanola, y con la coma abriria todo en una sola columna.
    const cabecera = ['Dia', 'Escaneos', 'Visitantes unicos'];
    const filas = datos.ordenados.map((d) => [d.day, d.scans, d.uniqueScans]);

    const extra: (string | number)[][] = [
      [],
      ['Pais', 'Escaneos'],
      ...Object.entries(datos.paises).map(([c, v]) => [nombrePais(c), v]),
      [],
      ['Dispositivo', 'Escaneos'],
      ...Object.entries(datos.dispositivos).map(([d, v]) => [
        NOMBRES_DISPOSITIVO[d] ?? d,
        v,
      ]),
      [],
      ['Hora (UTC)', 'Escaneos'],
      ...Object.entries(datos.horas)
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([h, v]) => [`${h}:00`, v]),
    ];

    const csv = [cabecera, ...filas, ...extra]
      .map((fila) => fila.join(';'))
      .join('\r\n');

    // El BOM hace que Excel reconozca UTF-8 y no destroce los acentos.
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `${nombreFichero}-${dayKey()}.csv`;
    enlace.click();
    URL.revokeObjectURL(url);
  }

  const paises: Reparto[] = datos
    ? Object.entries(datos.paises).map(([clave, valor]) => ({
        clave,
        etiqueta: nombrePais(clave),
        valor,
      }))
    : [];

  const dispositivos: Reparto[] = datos
    ? Object.entries(datos.dispositivos).map(([clave, valor]) => ({
        clave,
        etiqueta: NOMBRES_DISPOSITIVO[clave] ?? clave,
        valor,
      }))
    : [];

  return (
    <Card
      title={titulo}
      actions={
        <div className="rangos" role="group" aria-label="Periodo">
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`rangos__item${rango === d ? ' rangos__item--activo' : ''}`}
              aria-pressed={rango === d}
              onClick={() => onRango(d)}
            >
              {d} dias
            </button>
          ))}
        </div>
      }
    >
      {error ? <Callout tone="danger">{error}</Callout> : null}

      {datos === null ? (
        <div className="center">
          <Spinner label="Cargando estadísticas" />
        </div>
      ) : (
        <>
          <div className="tiles">
            <StatTile value={datos.total} label="escaneos" hint={`ultimos ${rango} dias`} />
            <StatTile
              value={datos.unicos}
              label="personas distintas"
              hint="contadas sin guardar su IP"
            />
            {typeof totalHistorico === 'number' ? (
              <StatTile value={totalHistorico} label="desde el inicio" />
            ) : null}
          </div>

          <AreaTemporal puntos={datos.serie} etiqueta="Escaneos por dia" />

          <div className="desgloses">
            <section>
              <h3 className="desgloses__titulo">Por dispositivo</h3>
              <BarrasReparto datos={dispositivos} />
            </section>

            <section>
              <h3 className="desgloses__titulo">Por país</h3>
              <BarrasReparto
                datos={paises}
                vacio="Todavia sin datos de pais. Se deduce de la conexion y no siempre esta disponible."
              />
            </section>
          </div>

          <section>
            <h3 className="desgloses__titulo">Por hora del día</h3>
            <ColumnasHora porHora={datos.horas} />
          </section>

          <div className="row" style={{ marginBlockStart: 'var(--qr-space-4)' }}>
            <Button variant="secondary" size="sm" onClick={exportarCsv}>
              Exportar a CSV
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setTabla(!tabla)}>
              {tabla ? 'Ocultar la tabla' : 'Ver como tabla'}
            </Button>
          </div>

          {/* La tabla no es un extra: es lo que hace legibles los datos a quien
              no distingue los colores o navega con lector de pantalla. */}
          {tabla ? (
            <div className="tabla-scroll">
              <table className="tabla">
                <caption className="qr-visually-hidden">
                  Escaneos y visitantes únicos por día
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Día</th>
                    <th scope="col">Escaneos</th>
                    <th scope="col">Personas distintas</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.ordenados.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        Sin datos en este periodo.
                      </td>
                    </tr>
                  ) : (
                    [...datos.ordenados].reverse().map((d) => (
                      <tr key={d.day}>
                        <td>{d.day}</td>
                        <td>{d.scans.toLocaleString('es')}</td>
                        <td>{d.uniqueScans.toLocaleString('es')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
